import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AlertCircle } from 'lucide-react';

import { useMDOverdueInvoices } from '../hooks/useMDOverdueInvoices';

/**
 * Overdue invoices — TS Lot 1 §7.1, Collection block.
 *
 * The Collection area shipped with an aging summary only, which the client
 * review AL-2026/08-04-01 recorded as finding D-04 ("No list of overdue
 * invoices"). This is that list: UNPAID bills whose due date has passed,
 * longest overdue first.
 *
 * Read-only — it renders `Bill` rows and never touches billing state. Invoices
 * with no due date are excluded by the server rather than assumed overdue.
 */

const formatUSD = (n: number) =>
  `$${Math.round(n).toLocaleString('en-US')}`;

const formatDate = (iso: string | null) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toISOString().slice(0, 10);
};

/** Age bands mirror the aging summary above so the two read consistently. */
function OverdueBadge({ days }: { days: number }) {
  const cls =
    days > 90
      ? 'bg-red-50 text-red-700 border-red-100'
      : days > 60
        ? 'bg-orange-50 text-orange-700 border-orange-200'
        : days > 30
          ? 'bg-amber-50 text-amber-700 border-amber-200'
          : 'bg-slate-50 text-slate-600 border-slate-200';
  return (
    <span
      className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[0.6875rem] font-medium tabular-nums ${cls}`}
    >
      {days}d
    </span>
  );
}

export function OverdueInvoicesTable() {
  const { t } = useTranslation('common');
  const { data, isLoading } = useMDOverdueInvoices();

  const invoices = data?.invoices ?? [];

  return (
    <div className="mt-5 border-t border-slate-100 pt-4">
      <div className="mb-2.5 flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-[0.875rem] font-semibold text-slate-900">
          <AlertCircle size={14} className="text-red-500" />
          {t('dashboard.overdueInvoices')}
        </div>
        {!isLoading && data && data.count > 0 && (
          <span className="text-[0.75rem] text-slate-500">
            {t('dashboard.overdueInvoicesSummary', {
              count: data.count,
              amount: formatUSD(data.totalOutstanding),
            })}
          </span>
        )}
      </div>

      {isLoading ? (
        <div className="space-y-1.5">
          {[0, 1, 2].map((i) => (
            <div key={i} className="h-8 animate-pulse rounded bg-slate-100" />
          ))}
        </div>
      ) : invoices.length === 0 ? (
        <p className="py-4 text-center text-[0.8125rem] text-slate-400">
          {t('dashboard.noOverdueInvoices')}
        </p>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-slate-200">
          <table className="w-full border-collapse text-sm">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50">
                {[
                  t('dashboard.invoice'),
                  t('client'),
                  t('dueDate'),
                  t('dashboard.overdueBy'),
                  t('dashboard.outstanding'),
                ].map((h) => (
                  <th
                    key={h}
                    className="whitespace-nowrap px-2 py-2 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-500 last:text-right"
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {invoices.map((inv) => (
                <tr
                  key={inv.id}
                  className="border-b border-slate-100 last:border-b-0 hover:bg-slate-50"
                >
                  <td className="px-2 py-2 font-semibold tabular-nums text-slate-500">
                    #{inv?.id}
                  </td>
                  <td className="px-2 py-2">
                    {inv?.clientId ? (
                      <Link
                        to={`/app/clients/${inv.clientId}`}
                        className="text-[#0069D1] hover:underline"
                      >
                        {inv?.clientName ?? '—'}
                      </Link>
                    ) : (
                      (inv?.clientName ?? '—')
                    )}
                  </td>
                  <td className="whitespace-nowrap px-2 py-2 tabular-nums text-slate-600">
                    {formatDate(inv?.dueAt)}
                  </td>
                  <td className="px-2 py-2">
                    <OverdueBadge days={inv?.daysOverdue ?? 0} />
                  </td>
                  <td className="px-2 py-2 text-right font-semibold tabular-nums text-slate-900">
                    {formatUSD(inv?.outstanding ?? 0)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* D1 — the list states what it counts, so "12 overdue" can be checked. */}
      {!isLoading && data && data.count > data.shown && (
        <p className="mt-2 text-[0.6875rem] text-slate-400">
          {t('dashboard.overdueInvoicesTruncated', {
            shown: data.shown,
            count: data.count,
          })}
        </p>
      )}
    </div>
  );
}
